import { generateId } from "@repo/core";
import type { Category, Transaction, TxType } from "./schema";

export type CsvColumnKey =
  | "date"
  | "amount"
  | "type"
  | "category"
  | "account"
  | "person"
  | "notes"
  | "skip";

export interface CsvParseResult {
  headers: string[];
  rows: string[][];
}

export interface CsvMapping {
  [header: string]: CsvColumnKey;
}

export interface CsvPreviewRow {
  date: string;
  amount: number;
  type: TxType;
  categoryId: string;
  categoryLabel: string;
  accountId: string;
  person: string;
  notes: string;
  valid: boolean;
  error?: string;
}

/** Optional translated error strings for row validation */
export interface CsvImportLabels {
  badDate?: string;
  badAmount?: string;
  noCategory?: string;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseCsvText(text: string): CsvParseResult {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQ = !inQ;
      } else if (ch === "," && !inQ) {
        out.push(cur.trim());
        cur = "";
      } else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(parseLine);
  return { headers, rows };
}

/** Lowercase, strip accents — matches BudgetOS exports in any launch locale. */
function normalizeHeader(raw: string): string {
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]/g, "");
}

function headerMatches(n: string, ...aliases: string[]): boolean {
  return aliases.some((a) => n === a || n.endsWith(a) || n.includes(a));
}

/** Guess mapping from common bank export header names (EN + launch locales). */
export function suggestMapping(headers: string[]): CsvMapping {
  const map: CsvMapping = {};
  for (const h of headers) {
    const n = normalizeHeader(h);
    if (
      headerMatches(
        n,
        "date",
        "fecha",
        "datum",
        "data",
        "posted",
        "transactiondate",
        "valuedate"
      )
    ) {
      map[h] = "date";
    } else if (
      headerMatches(
        n,
        "amount",
        "importe",
        "importo",
        "montant",
        "betrag",
        "valor",
        "value",
        "debitcredit",
        "sum"
      )
    ) {
      map[h] = "amount";
    } else if (
      headerMatches(n, "type", "tipo", "typ", "creditordebit", "inout")
    ) {
      map[h] = "type";
    } else if (
      headerMatches(n, "category", "categoria", "categorie", "kategorie", "cat")
    ) {
      map[h] = "category";
    } else if (
      headerMatches(n, "account", "cuenta", "compte", "conto", "konto", "acct")
    ) {
      map[h] = "account";
    } else if (
      headerMatches(n, "person", "persona", "personne", "pessoa", "payee", "member")
    ) {
      map[h] = "person";
    } else if (
      headerMatches(
        n,
        "notes",
        "notas",
        "note",
        "notizen",
        "memo",
        "description",
        "narration",
        "details",
        "desc"
      )
    ) {
      map[h] = "notes";
    } else {
      map[h] = "skip";
    }
  }
  return map;
}

function normalizeDate(raw: string): string | null {
  const t = raw.trim();
  if (DATE_RE.test(t)) return t;
  // MM/DD/YYYY or DD/MM/YYYY — prefer ISO-ish US when ambiguous
  const m = t.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const y = m[3];
    // If first > 12 treat as D/M/Y else M/D/Y
    if (a > 12) return `${y}-${String(b).padStart(2, "0")}-${String(a).padStart(2, "0")}`;
    return `${y}-${String(a).padStart(2, "0")}-${String(b).padStart(2, "0")}`;
  }
  const d = new Date(t);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function normalizeAmount(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.,\-]/g, "").replace(/,/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : Math.abs(n);
}

function normalizeType(raw: string, amountSigned: string): TxType {
  const n = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
  if (
    n.includes("income") ||
    n.includes("ingreso") ||
    n.includes("ingres") ||
    n.includes("revenu") ||
    n.includes("einnahme") ||
    n.includes("receita") ||
    n.includes("entrata") ||
    n.includes("credit") ||
    /\bin\b/.test(n)
  ) {
    return "income";
  }
  if (
    n.includes("transfer") ||
    n.includes("transferencia") ||
    n.includes("transfert") ||
    n.includes("uberweis")
  ) {
    return "transfer";
  }
  if (
    n.includes("expense") ||
    n.includes("gasto") ||
    n.includes("gastos") ||
    n.includes("depense") ||
    n.includes("ausgabe") ||
    n.includes("despesa") ||
    n.includes("spesa") ||
    n.includes("debit") ||
    n.includes("out")
  ) {
    return "expense";
  }
  if (amountSigned.trim().startsWith("-")) return "expense";
  return "expense";
}

export function buildPreview(
  parsed: CsvParseResult,
  mapping: CsvMapping,
  categories: Category[],
  defaultAccountId: string,
  defaultPerson = "Household",
  labels?: CsvImportLabels
): CsvPreviewRow[] {
  const badDate = labels?.badDate ?? "Bad date";
  const badAmount = labels?.badAmount ?? "Bad amount";
  const noCategory = labels?.noCategory ?? "No category";

  const idx = (key: CsvColumnKey) =>
    parsed.headers.findIndex((h) => mapping[h] === key);

  const iDate = idx("date");
  const iAmt = idx("amount");
  const iType = idx("type");
  const iCat = idx("category");
  const iAcc = idx("account");
  const iPerson = idx("person");
  const iNotes = idx("notes");

  const fallbackExpense = categories.find((c) => c.type === "expense");
  const fallbackIncome = categories.find((c) => c.type === "income");

  return parsed.rows.map((row) => {
    const rawDate = iDate >= 0 ? row[iDate] ?? "" : "";
    const rawAmt = iAmt >= 0 ? row[iAmt] ?? "" : "";
    const rawType = iType >= 0 ? row[iType] ?? "" : "";
    const rawCat = iCat >= 0 ? row[iCat] ?? "" : "";
    const date = normalizeDate(rawDate);
    const amount = normalizeAmount(rawAmt);
    const type = normalizeType(rawType, rawAmt);

    let category =
      categories.find((c) => c.name.toLowerCase() === rawCat.toLowerCase()) ||
      (type === "income" ? fallbackIncome : fallbackExpense);

    if (!date || amount === null || !category) {
      return {
        date: date ?? rawDate,
        amount: amount ?? 0,
        type,
        categoryId: category?.id ?? "",
        categoryLabel: rawCat || "—",
        accountId: defaultAccountId,
        person: defaultPerson,
        notes: iNotes >= 0 ? row[iNotes] ?? "" : "",
        valid: false,
        error: !date ? badDate : amount === null ? badAmount : noCategory,
      };
    }

    return {
      date,
      amount,
      type,
      categoryId: category.id,
      categoryLabel: category.name,
      accountId: defaultAccountId,
      person: (iPerson >= 0 ? row[iPerson] : "") || defaultPerson,
      notes: iNotes >= 0 ? row[iNotes] ?? "" : "",
      valid: true,
    };
  });
}

export function previewToTransactions(rows: CsvPreviewRow[]): Transaction[] {
  return rows
    .filter((r) => r.valid)
    .map((r) => ({
      id: generateId(),
      date: r.date,
      type: r.type,
      amount: r.amount,
      categoryId: r.categoryId,
      accountId: r.accountId,
      person: r.person,
      notes: r.notes,
      tags: ["csv-import"],
    }));
}
